import { getPreviousData } from '@/dashboards/shared/optimisation/previousData';
import type { OptimizedPlan } from '@/dashboards/shared/optimisation/optimisationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import {
    structureBudgetData,
    structureMonthlyMediaInvestmentData,
} from '@/dashboards/utils/data';
import { withUncategorisedChannels } from '@/dashboards/utils/data/withUncategorisedChannels';
import { useDuckDBQuery } from '@/duckdb';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import moment from 'moment';
import { useMemo } from 'react';

// The yearly optimisation always covers one year from the dashboard's configured start.
export const optimisationPeriod = (
    yearlyBudgetOptimisationPeriod: moment.MomentInput,
) => ({
    startDate: moment(yearlyBudgetOptimisationPeriod).format('DD-MM-YYYY'),
    endDate: moment(yearlyBudgetOptimisationPeriod)
        .add(1, 'year')
        .subtract(1, 'day')
        .format('DD-MM-YYYY'),
});

// Everything the page renders, derived from the committed run only.
export const useBudgetOptimizationData = (
    optimizedData: OptimizedPlan | undefined,
) => {
    const {
        project: { interval },
        dashboard: { yearlyBudgetOptimisationPeriod },
        endDate: dataEndDate,
        variableNameMap,
    } = useDashboardGlobalContext();
    const { optimizer } = useOptimizer();

    const { startDate: startDateParam, endDate: endDateParam } =
        optimisationPeriod(yearlyBudgetOptimisationPeriod);

    const prevYearEndDate = dataEndDate;
    const prevYearStartDate = moment(dataEndDate, 'DD-MM-YYYY')
        .subtract(1, 'year')
        .add(1, 'day')
        .format('DD-MM-YYYY');

    const {
        data: fetchedPrevData,
        isFetching: prevDataIsFetching,
        isLoading: prevDataIsLoading,
    } = useDuckDBQuery({
        queryKey: ['prev-data', prevYearStartDate, prevYearEndDate, interval],
        queryFn: () =>
            getPreviousData({
                startDate: prevYearStartDate,
                endDate: prevYearEndDate,
                interval: interval as 'weekly' | 'daily',
            }),
        enabled: !!optimizedData,
    });

    const prevData = useMemo(
        () =>
            fetchedPrevData
                ? withUncategorisedChannels(fetchedPrevData, optimizedData)
                : fetchedPrevData,
        [fetchedPrevData, optimizedData],
    );

    const monthlyMediaInvestment = useMemo(() => {
        const mediasInRoi = Object.keys(
            prevData?.roiCurves.variablesAndCategories.byVariable || {},
        );

        const rowsInRoi = Object.entries(prevData?.rows || {}).reduce(
            (acc, [key, row]) => {
                acc[key] = Object.keys(row).reduce(
                    (filtered, key) => {
                        if (mediasInRoi.includes(key)) {
                            filtered[key] = row[key];
                        }
                        return filtered;
                    },
                    {} as typeof rowsInRoi,
                );

                return acc;
            },
            {},
        );

        return structureMonthlyMediaInvestmentData({
            optimizedData,
            rows: rowsInRoi,
            startDate: startDateParam,
        });
    }, [
        optimizedData,
        prevData?.roiCurves.variablesAndCategories.byVariable,
        prevData?.rows,
        startDateParam,
    ]);

    const structuredBudgetData = useMemo(
        () =>
            structureBudgetData({
                optimizedData,
                variablesAndCategories: prevData?.roiCurves
                    .variablesAndCategories || {
                    byCategory: {},
                    byVariable: {},
                },
                totals: prevData?.totals || {},
                variableNameMap,
            }),
        [
            variableNameMap,
            optimizedData,
            prevData?.roiCurves.variablesAndCategories,
            prevData?.totals,
        ],
    );

    const initialAllocation = useMemo(() => {
        return optimizer.getInitialAllocation();
    }, [optimizer]);

    const diminishingPoints = useMemo(() => {
        return optimizer.getDiminishingPoints();
    }, [optimizer]);

    return {
        data: {
            optimized: optimizedData,
            previous: prevData,
            monthlyMediaInvestment,
            initialAllocation,
            diminishingPoints,
            optimizationStartDate: startDateParam,
            optimizationEndDate: endDateParam,
            historicDataStartDate: prevYearStartDate,
            historicDataEndDate: prevYearEndDate,
            ...structuredBudgetData,
        },
        isFetching: prevDataIsFetching,
        isLoading: prevDataIsLoading,
    };
};

export type UseBudgetOptimizationQueryResponse = ReturnType<
    typeof useBudgetOptimizationData
>;
